import { useEffect, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { useControls } from 'leva'

import { selectedProductStore, useStore, getProductObject, productSpin, showcaseState, getBubbleEl, productSpinPad, getBraccio, watchOpenStore } from '../../store/uiStore'
import { IS_TOUCH } from '../../config/touch'
import { tieniInMano, cfgPresa } from './presaInMano'

// ============================================================
// Vetrina 3D: con la pagina prodotto aperta, la VERA scatola
// dello scaffale vola davanti alla camera (a sinistra, la scheda
// specifiche sta a destra) e si può ruotare trascinando il mouse
// (productSpin, scritto da ProductPage). Alla chiusura torna
// esattamente al suo posto sullo scaffale.
// ============================================================

// ============================================================
// DOVE STA IL PRODOTTO IN VETRINA, e perche' questi numeri.
//
// Erano tre valori tarati a occhio (distanza 1.6, altezza 0.05, scala 1.1).
// Da quando il prodotto sta IN MANO li decide il braccio, che e' lungo 0.881
// dalla spalla e ha la spalla 0.69 SOTTO l'occhio: quella geometria dice sia
// quanto lontano puo' stare il prodotto, sia — ed e' la parte che non si
// indovina — QUANTO SI PUO' INCLINARE LA VISUALE prima che la mano non ci
// arrivi piu'. Misure e tabelle in docs/negozio-3d.md.
//
//  distanza  metri davanti all'occhio — ed e' anche quanto la MANO sta lontana
//            dalla faccia, visto che la mano sta dietro al prodotto. Segnalato
//            («voglio che la mano sia piu' distante dalla faccia») e pagato con
//            la fascia: allontanare il prodotto SPOSTA IN BASSO le inclinazioni
//            che il braccio riesce a servire.
//  sinistra  quello che conta e' l'ANGOLO, non i metri: 0.12 a 0.70 sono 10
//            gradi a sinistra del centro, gli stessi che erano 0.35 a 1.6.
//  altezza   il prodotto va sotto la linea degli occhi, perche' e' li' che
//            viene la mano a braccio piegato — e perche' abbassarlo AVVICINA il
//            bersaglio alla spalla e quindi regala distanza in avanti: a parita'
//            di tutto il resto, da -0.12 a -0.18 la mano guadagna 2.5 cm.
//  scala     un oggetto che sta NEL PALMO. A 0.45 il lato piu' lungo veniva
//            quanto la mano intera (0.386) e la mano spariva dietro; a 0.28
//            viene 0.20, cioe' della misura del palmo, e la mano si vede
//            attorno. E' la terza volta che si scende: prima 0.84, poi 0.45.
//  pitchSu/Giu  fin dove il prodotto insegue la visuale. Oltre resta dov'e',
//            come un oggetto tenuto in mano davvero.
// ============================================================
// Quanto al massimo il prodotto si puo' spostare da dove lo mette la vetrina,
// per rispettare il braccio (vedi il tetto nel correttore, piu' sotto).
const MAX_CORREZIONE = 0.25

export const cfgVetrina = {
  distanza: 0.70,
  sinistra: 0.22,
  altezza: -0.3,
  scala: 0.28,
  // la fascia NON si sceglie: e' quella che il braccio serve a questa distanza
  // e a questa quota, misurata cursori alla mano (vedi docs/negozio-3d.md)
  // PIU' VICINO DI COSI' AL CORPO IL PRODOTTO NON VIENE. Segnalato: «quando
  // cammino avanti e guardo giu' si bugga tutto il braccio perche' il prodotto
  // viene troppo vicino al corpo». Misurato in piedi e fermi: guardando in
  // basso il prodotto arriva a 0.612 dalla spalla, mentre il fermo del gomito
  // (82 gradi) ne pretende almeno 0.584 — meno di tre centimetri di margine.
  // Camminando il passo fa oscillare corpo e camera e quel margine sparisce:
  // il bersaglio finisce dentro la sfera che il braccio non puo' raggiungere,
  // la cinematica inversa non lo puo' soddisfare e il braccio si accartoccia.
  // Questo e' un fondo DURO, applicato al fotogramma: il correttore integrale
  // qui non basta, perche' insegue con un ritardo e il passo e' piu' veloce di
  // lui. Misurato che a 15 gradi sotto l'orizzonte il prodotto sta a 0.666 e il
  // braccio a 0.619 (comodo), quindi 0.68 non tocca le inquadrature normali e
  // interviene solo dove serve.
  minimoDalCorpo: 0.68,
  pitchSu: 3,
  pitchGiu: 28,
  velocita: 8,
}

// In ?debug si tara da console: window.__vetrina.distanza = 0.7, ecc.
if (typeof window !== 'undefined' && /[?#]debug/.test(window.location.search + window.location.hash)) {
  window.__vetrina = cfgVetrina
}

export default function ProductShowcase() {
  // Posa della scatola in vetrina. I cursori scrivono nell'oggetto di modulo
  // qui sopra solo quando cambiano davvero, cosi' resta scrivibile da console
  // (window.__vetrina in ?debug) — stessa convenzione di cfgPresa, ed e' quella
  // che permette di TARARE MISURANDO invece che a occhio.
  const cfgV = useControls('Vetrina prodotto', {
    distanza: { value: cfgVetrina.distanza, min: 0.3, max: 5, step: 0.05 },
    sinistra: { value: cfgVetrina.sinistra, min: -2, max: 3, step: 0.05 },
    altezza: { value: cfgVetrina.altezza, min: -1.5, max: 1.5, step: 0.01, label: 'alza/abbassa il prodotto' },
    scala: { value: cfgVetrina.scala, min: 0.1, max: 3, step: 0.05 },
    minimoDalCorpo: { value: cfgVetrina.minimoDalCorpo, min: 0.3, max: 1.2, step: 0.01, label: 'non si avvicina oltre' },
    pitchSu: { value: cfgVetrina.pitchSu, min: 0, max: 60, step: 1, label: 'segue in alto (gradi)' },
    pitchGiu: { value: cfgVetrina.pitchGiu, min: 0, max: 60, step: 1, label: 'segue in basso (gradi)' },
    velocita: { value: cfgVetrina.velocita, min: 1, max: 15, step: 0.5 },
  }, { collapsed: true })
  const precVetrina = useRef({})
  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const k in cfgV) {
      if (cfgV[k] !== precVetrina.current[k]) { cfgVetrina[k] = cfgV[k]; precVetrina.current[k] = cfgV[k] }
    }
  }, [cfgV])

  // La presa: quanto la mano sta dietro al prodotto, dove va il gomito, quanto
  // si curvano le dita. I cursori scrivono nell'oggetto di modulo solo quando
  // cambiano davvero, cosi' resta scrivibile da console (window.__presa).
  const cfgP = useControls('Presa', {
    attiva: true,
    dietro: { value: cfgPresa.dietro, min: 0, max: 0.4, step: 0.01, label: 'mano dietro al prodotto' },
    giu: { value: cfgPresa.giu, min: -0.4, max: 0.4, step: 0.01, label: 'prodotto piu\' in alto della mano' },
    ancora: { value: cfgPresa.ancora, min: 0.5, max: 3, step: 0.1, label: 'appoggio verso le dita' },
    // passo fine: un centimetro di scostamento sposta il polso di parecchio piu'
    // di un centimetro, perche' il punto d'appoggio e' agganciato al prodotto e
    // a muoversi e' la mano (misurato: 0.06 sopra il palmo = 0.40 di polso)
    appoggioLato: { value: cfgPresa.appoggioLato, min: -0.08, max: 0.08, step: 0.002, label: 'appoggio verso il pollice' },
    appoggioSopra: { value: cfgPresa.appoggioSopra, min: -0.08, max: 0.08, step: 0.002, label: 'appoggio sopra il palmo' },
    scalaOrologio: { value: cfgPresa.scalaOrologio, min: 0.4, max: 1.3, step: 0.02, label: 'grandezza orologio' },
    palmoSu: { value: cfgPresa.palmoSu, min: 0, max: 1, step: 0.05, label: 'palmo rivolto in su' },
    poloGiu: { value: cfgPresa.poloGiu, min: 0, max: 2, step: 0.05, label: 'gomito in giu\'' },
    poloDietro: { value: cfgPresa.poloDietro, min: -1, max: 2, step: 0.05, label: 'gomito indietro' },
    gomitoMin: { value: cfgPresa.gomitoMin, min: 20, max: 110, step: 1, label: 'gomito: piega massima' },
    torsioneAvambraccio: { value: cfgPresa.torsioneAvambraccio, min: 0, max: 1, step: 0.05, label: 'torsione sull\'avambraccio' },
    torsioneMax: { value: cfgPresa.torsioneMax, min: 0, max: 120, step: 1, label: 'torsione massima (gradi)' },
    piegaPolso: { value: cfgPresa.piegaPolso, min: -40, max: 60, step: 1, label: 'piega del polso' },
    clavicola: { value: cfgPresa.clavicola, min: 0, max: 1, step: 0.05, label: 'spalla che si porta avanti' },
    clavicolaMax: { value: cfgPresa.clavicolaMax, min: 0, max: 45, step: 1, label: 'tetto spalla (gradi)' },
    dita1: { value: cfgPresa.dita1, min: -20, max: 90, step: 1 },
    dita2: { value: cfgPresa.dita2, min: -20, max: 90, step: 1 },
    dita3: { value: cfgPresa.dita3, min: -20, max: 90, step: 1 },
    pollice1: { value: cfgPresa.pollice1, min: -40, max: 60, step: 1 },
    pollice2: { value: cfgPresa.pollice2, min: -40, max: 60, step: 1 },
  }, { collapsed: true })
  const precPresa = useRef({})
  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const k in cfgP) {
      if (cfgP[k] !== precPresa.current[k]) { cfgPresa[k] = cfgP[k]; precPresa.current[k] = cfgP[k] }
    }
  }, [cfgP])
  // quanto la presa e' "entrata": sale prendendo, scende rimettendo a posto
  const presa = useRef(0)
  // di quanto il prodotto deve scendere perche' la mano ci arrivi (vedi sotto)
  const scartoPresa = useRef(new THREE.Vector3())

  const selected = useStore(selectedProductStore)
  const { camera } = useThree()
  // prodotto attualmente "in mano": oggetto + trasform originale dello scaffale
  const active = useRef(null)

  // Telefono in verticale: la scheda è un bottom-sheet → il prodotto
  // va centrato e un po' più in alto, non di lato
  const [portrait, setPortrait] = useState(
    typeof window !== 'undefined' && window.innerHeight > window.innerWidth
  )
  useEffect(() => {
    const onResize = () => setPortrait(window.innerHeight > window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // vettori riusati per non allocare ogni frame
  const tmp = useRef({
    camPos: new THREE.Vector3(),
    camQuat: new THREE.Quaternion(),
    target: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    spin: new THREE.Quaternion(),
    euler: new THREE.Euler(),
    scale: new THREE.Vector3(),
    proj: new THREE.Vector3(),
    sguardo: new THREE.Vector3(),
    destra: new THREE.Vector3(),
    eulPosa: new THREE.Euler(0, 0, 0, 'YXZ'),
    qPosa: new THREE.Quaternion(),
    spalla: new THREE.Vector3(),
    fuori: new THREE.Vector3(),
    camPrec: new THREE.Vector3(),
    passo: new THREE.Vector3(),
    camValida: false,
  }).current

  // il carosello non deve restare "acceso" se la vetrina si smonta
  useEffect(() => () => { showcaseState.active = false }, [])

  useFrame((state, delta) => {
    const k = 1 - Math.exp(-cfgVetrina.velocita * delta) // smoothing indipendente dal framerate
    // LA MANO SI MUOVE QUI E NON NEL PLAYER, anche se le ossa sono le sue:
    // questo useFrame gira DOPO quello del Player nello stesso fotogramma,
    // quindi la camera e' gia' al suo posto, il prodotto pure, e la mano puo'
    // mettersi esattamente dietro invece di inseguire con un frame di ritardo.
    // Col polso alzato la presa si spegne: l'orologio sta sullo stesso braccio.
    const vuolePresa = cfgPresa.attiva && !!selected && !watchOpenStore.get()
    presa.current += ((vuolePresa ? 1 : 0) - presa.current) *
      (1 - Math.exp(-delta / Math.max(0.03, cfgPresa.tempo)))

    // Nuovo prodotto selezionato → prendi la sua scatola dallo scaffale
    if (selected && active.current?.id !== selected.id) {
      const obj = getProductObject(selected.id)
      if (obj) {
        if (active.current) putBack(active.current) // l'eventuale precedente torna a posto
        active.current = {
          id: selected.id,
          obj,
          homePos: obj.position.clone(),
          homeQuat: obj.quaternion.clone(),
          homeScale: obj.scale.clone(),
        }
        productSpin.rx = 0
        productSpin.ry = 0
        productSpinPad.x = 0
        productSpinPad.y = 0
      }
    }

    // Mini-joystick della nuvoletta: velocità di rotazione proporzionale
    // allo spostamento del pomello (0,0 quando è rilasciato)
    productSpin.ry += productSpinPad.x * delta * 3
    productSpin.rx = THREE.MathUtils.clamp(productSpin.rx + productSpinPad.y * delta * 3, -1.2, 1.2)

    const a = active.current
    if (!a) return

    if (selected && selected.id === a.id) {
      // --- In vetrina: punto davanti alla camera, spostato a sinistra ---
      camera.getWorldPosition(tmp.camPos)
      camera.getWorldQuaternion(tmp.camQuat)
      const offX = IS_TOUCH && portrait ? 0 : cfgVetrina.sinistra
      const offY = IS_TOUCH && portrait ? 0.35 : cfgVetrina.altezza
      // LA POSA SEGUE LO SGUARDO CON L'INCLINAZIONE LIMITATA (vedi i cursori).
      // Yaw e pitch si ricavano dagli assi e non da camera.rotation: i
      // PointerLockControls scrivono il quaternione da un euler YXZ, mentre
      // camera.rotation si decompone nell'ordine proprio della camera. E lo yaw
      // si legge dall'asse DESTRO, che resta orizzontale a ogni inclinazione,
      // non da quello di sguardo, che guardando a piombo degenera nel rumore.
      // (Stessa regola in Player.jsx e cameraShake.js: se si tocca una, toccare
      // anche le altre.)
      tmp.sguardo.set(0, 0, 1).applyQuaternion(tmp.camQuat)
      tmp.destra.set(1, 0, 0).applyQuaternion(tmp.camQuat)
      tmp.eulPosa.set(
        THREE.MathUtils.clamp(
          Math.asin(THREE.MathUtils.clamp(-tmp.sguardo.y, -1, 1)),
          -THREE.MathUtils.degToRad(cfgVetrina.pitchGiu),
          THREE.MathUtils.degToRad(cfgVetrina.pitchSu),
        ),
        Math.atan2(-tmp.destra.z, tmp.destra.x),
        0,
      )
      tmp.qPosa.setFromEuler(tmp.eulPosa)
      tmp.target.set(-offX, offY, -cfgVetrina.distanza).applyQuaternion(tmp.qPosa).add(tmp.camPos)
      // IL PRODOTTO RISPETTA IL BRACCIO: se la mano non ci arriva (guardando in
      // alto il bersaglio esce dalla portata — misurato 16 cm a 30 gradi sopra
      // l'orizzonte) il prodotto si abbassa di quello scarto invece di
      // galleggiare staccato dal palmo. Lo scarto lo restituisce tieniInMano in
      // fondo al fotogramma; qui si applica filtrato, cosi' e' una correzione
      // morbida e non un inseguimento nervoso.
      tmp.target.add(scartoPresa.current)
      // ...e comunque non piu' vicino di tanto alla SPALLA, che e' l'unico
      // punto che conti: e' da li' che parte il braccio, ed e' li' che il
      // prodotto diventa irraggiungibile. Fondo duro e istantaneo, non
      // filtrato: camminando il passo e' piu' veloce di qualunque inseguimento.
      // IL PRODOTTO CAMMINA CON TE, non si fa trascinare.
      // Senza questo il prodotto insegue la camera con lo smorzamento del
      // lerp, e camminando resta indietro di circa velocita/velocitaLerp:
      // MISURATO a 3 unita' al secondo, 0.34 — che e' precisamente quanto si
      // avvicinava al corpo. E' questa la causa vera di «cammino avanti e
      // guardo giu' e si bugga tutto il braccio»: non il guardare in basso,
      // ma il camminare. Sommando lo spostamento della camera prima di
      // smorzare, il lerp deve correggere solo quel che e' cambiato DAVVERO
      // (l'inclinazione, i cursori), e il ritardo sparisce.
      if (tmp.camValida) a.obj.position.add(tmp.passo.copy(tmp.camPos).sub(tmp.camPrec))
      a.obj.position.lerp(tmp.target, k)
      // ...E COMUNQUE NON PIU' VICINO DI TANTO ALLA SPALLA. Il fondo si applica
      // alla posizione VERA e non al bersaglio: sul bersaglio non garantisce
      // niente, perche' fra il bersaglio e dove il prodotto si trova davvero
      // c'e' sempre uno smorzamento di mezzo. La spalla e' l'unico punto che
      // conti: e' da li' che parte il braccio, ed e' li' che il prodotto
      // diventa irraggiungibile.
      const br = getBraccio()
      if (br && cfgVetrina.minimoDalCorpo > 0) {
        br.spalla.getWorldPosition(tmp.spalla)
        tmp.fuori.copy(a.obj.position).sub(tmp.spalla)
        const quanto = tmp.fuori.length()
        if (quanto > 1e-4 && quanto < cfgVetrina.minimoDalCorpo) {
          a.obj.position.copy(tmp.spalla)
            .addScaledVector(tmp.fuori.divideScalar(quanto), cfgVetrina.minimoDalCorpo)
        }
      }

      // fronte verso la camera + rotazione del trascinamento utente
      tmp.euler.set(productSpin.rx, productSpin.ry, 0, 'YXZ')
      tmp.spin.setFromEuler(tmp.euler)
      tmp.quat.copy(tmp.camQuat).multiply(tmp.spin)
      a.obj.quaternion.slerp(tmp.quat, k)

      tmp.scale.copy(a.homeScale).multiplyScalar(cfgVetrina.scala)
      a.obj.scale.lerp(tmp.scale, k)

      tmp.camPrec.copy(tmp.camPos)
      tmp.camValida = true

      // posizione corrente per il carosello di media che orbita attorno
      showcaseState.active = true
      showcaseState.x = a.obj.position.x
      showcaseState.y = a.obj.position.y
      showcaseState.z = a.obj.position.z
      // il carosello dei media orbita attorno al prodotto: ora che il prodotto
      // e' in mano, e quindi piu' piccolo e piu' vicino, deve rimpicciolire con
      // lui o le card verrebbero a sbattere contro la camera
      showcaseState.scala = cfgVetrina.scala
      // il carosello dei media si dimensiona sulla DISTANZA, non sulla scala
      showcaseState.distanza = cfgVetrina.distanza

      // nuvoletta DOM: proietta il punto sopra il prodotto sullo schermo
      const bubble = getBubbleEl()
      if (bubble) {
        // il punto sopra il prodotto scala con lui: ora che sta in mano ed e'
        // piu' piccolo, 0.55 fissi lo avrebbero mandato a mezzo schermo di
        // distanza dalla scatola
        const sopra = 0.55 * (cfgVetrina.scala / 1.1)
        tmp.proj.set(a.obj.position.x, a.obj.position.y + sopra, a.obj.position.z).project(camera)
        const { width, height } = state.size
        // MARGINI SULLA MISURA VERA DELLA NUVOLETTA, non numeri fissi: con
        // 150 e 110 una nuvoletta larga 322 e alta ~125 usciva di 11 px ai
        // lati e restava incollata al bordo alto (visto col modello alto
        // della maglietta). offsetWidth/Height non forzano un ricalcolo:
        // qui si scrive solo il transform, che non sporca il layout.
        const bordo = 12
        const mezza = bubble.offsetWidth / 2 + bordo
        const x = THREE.MathUtils.clamp((tmp.proj.x * 0.5 + 0.5) * width, mezza, width - mezza)
        const y = THREE.MathUtils.clamp((-tmp.proj.y * 0.5 + 0.5) * height,
          bubble.offsetHeight + bordo, height - bordo)
        bubble.style.transform = `translate(-50%, -100%) translate(${x}px, ${y}px)`
        bubble.style.opacity = '1'
      }
    } else {
      showcaseState.active = false
      tmp.camValida = false
      // --- Pagina chiusa: la scatola torna sullo scaffale ---
      a.obj.position.lerp(a.homePos, k)
      a.obj.quaternion.slerp(a.homeQuat, k)
      a.obj.scale.lerp(a.homeScale, k)
      if (a.obj.position.distanceTo(a.homePos) < 0.01) {
        putBack(a) // aggancio finale esatto
        active.current = null
      }
    }

    // --- LA MANO DIETRO AL PRODOTTO ---
    // In fondo al fotogramma, quando il prodotto e' gia' dove deve stare: il
    // braccio ci si piega dietro con una cinematica inversa (presaInMano.js).
    // Resta acceso anche mentre la scatola torna sullo scaffale, con il peso
    // che scende: cosi' la mano l'accompagna invece di mollarla di scatto.
    if (presa.current > 0.002 && active.current) {
      // `congela`: col prodotto lasciato, la mano non lo insegue mentre torna
      // sullo scaffale — tiene l'ultimo bersaglio e si spegne dov'era.
      const scarto = tieniInMano(
        getBraccio(), active.current.obj, camera, presa.current, delta,
        !selected || selected.id !== active.current.id,
      )
      // INTEGRALE CON PERDITA, non proporzionale: la correzione si ACCUMULA
      // finche' la mano non arriva (uno scarto proporzionale si ferma a meta'
      // strada — misurato: 0.086 di stacco invece di 0.164, ma pur sempre
      // stacco) e si rilassa piano appena il braccio ci arriva di nuovo, cosi'
      // guardando di nuovo in basso il prodotto torna dov'e' tarato.
      if (scarto) {
        // ZONA MORTA di un centimetro: sotto quella soglia si considera che la
        // mano ci sia arrivata. Senza, l'integratore rincorre anche il residuo
        // di un paio di centimetri e nel tempo spinge il prodotto ben oltre la
        // distanza tarata (misurato: 0.98 dall'occhio invece di 0.82).
        const forza = Math.max(0, scarto.length() - 0.01)
        scartoPresa.current.multiplyScalar(Math.max(0, 1 - 1.5 * delta))
        if (forza > 0) {
          scartoPresa.current.addScaledVector(
            scarto.normalize(), forza * Math.min(1, 12 * delta),
          )
        }
        // TETTO ALLA CORREZIONE, altrimenti l'integratore SCAPPA.
        // Segnalato: «quando prendo il prodotto fa un giro strano intorno alla
        // testa che non si vede, spunta dopo un po'». Registrata la posizione
        // del prodotto fotogramma per fotogramma durante la presa: partiva da
        // 7.35 davanti, sfiorava l'occhio (z -0.06) e si fermava a **z +2.14**,
        // cioe' due metri DIETRO LA TESTA, dove restava per 132 fotogrammi su
        // 232 prima di rientrare oscillando.
        // Il motivo e' che l'errore puo' essere STRUTTURALMENTE inchiudibile:
        // il bersaglio del palmo e' definito rispetto al prodotto, quindi
        // spostando il prodotto si sposta anche il bersaglio, e se il braccio
        // non puo' soddisfarlo (per esempio col fermo del gomito stretto e la
        // mano chiesta molto sotto il prodotto) l'integratore spinge senza mai
        // chiudere, finche' non e' andato troppo in la' e l'errore cambia segno:
        // oscillazione, non convergenza. Un integratore senza tetto fa sempre
        // cosi'. Il tetto non toglie niente ai casi buoni — la correzione utile
        // misurata non ha mai passato i 16 cm — e trasforma il caso cattivo in
        // uno scostamento fisso invece che in una fuga.
        if (scartoPresa.current.lengthSq() > MAX_CORREZIONE * MAX_CORREZIONE) {
          scartoPresa.current.setLength(MAX_CORREZIONE)
        }
      }
    } else {
      scartoPresa.current.multiplyScalar(Math.exp(-6 * delta))
    }
  })

  return null
}

function putBack(a) {
  a.obj.position.copy(a.homePos)
  a.obj.quaternion.copy(a.homeQuat)
  a.obj.scale.copy(a.homeScale)
}
